/**
 * Dependency imports
 */
import React from 'react';
import {
  Anchor,
  Box,
  BoxProps,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { usePasswordReset } from '@/hooks/use-password-reset';
import Joi from 'joi';
import { joiResolver, useForm } from '@mantine/form';
import Button from '../Button';
import FAIcon from '../FAIcon';

/**
 * Local imports
 */

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<ResetPassFormProps>>((props) => css``);

/**
 * Component(s)
 */

/**
 * ResetPassForm Component
 */
interface Props {
  onLoginRequest?: () => void;
  onCloseRequest?: () => void;
  children?: React.ReactNode;
}

export type ResetPassFormProps = Props & BoxProps;

const ResetPassForm: React.FC<ResetPassFormProps> = ({
  onLoginRequest,
  onCloseRequest,
  ...props
}) => {
  const [resetPassword, resetPasswordResponse] = usePasswordReset();

  const schema = Joi.object({
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        'string.empty': 'Please enter an e-mail address',
        'string.email': 'Please enter a valid e-mail address',
      }),
  });

  const resetPasswordForm = useForm({
    initialValues: {
      email: '',
    },

    validate: joiResolver(schema),
  });

  const pending =
    resetPasswordResponse.status === 'pending' ||
    resetPasswordResponse.status === 'complete';

  if (resetPasswordResponse.status === 'complete') {
    return (
      <Root {...props} p={30}>
        <Title size={32}>Email Sent</Title>

        <Text mt={30}>
          Please check your inbox (or spam folder) and follow the instructions
          provided to reset your password.
        </Text>

        <Text mt={30} fz={12} opacity={0.5}>
          You can safely close this window.
        </Text>

        <Box mt={30}>
          <Button
            component="button"
            fullwidth
            transition={false}
            color="dark"
            onClick={onCloseRequest}
          >
            Close
          </Button>
        </Box>
      </Root>
    );
  }

  return (
    <Root {...props} p={30}>
      <Title size={32}>Forgot Password</Title>

      <Text opacity={0.7} fz={14} mt={5}>
        Please enter your e-mail to continue
      </Text>

      <Divider my={20} />

      <form
        onSubmit={resetPasswordForm.onSubmit(async ({ email }) => {
          resetPassword(email);
        })}
      >
        <Stack>
          <TextInput
            label={<strong>Email</strong>}
            placeholder="E-mail address"
            disabled={pending}
            {...resetPasswordForm.getInputProps('email')}
          />

          <Box mt={10}>
            <Button
              component="button"
              fullwidth
              transition={false}
              color="dark"
              type="submit"
              disabled={pending}
            >
              Send Reset Link
            </Button>
          </Box>
        </Stack>

        <Box mt={20}>
          <Anchor c="dark" opacity={0.6} onClick={onLoginRequest}>
            <Group justify="center" gap={10}>
              <FAIcon name="chevron-left" size={10} opacity={0.5} />
              <Text size="sm" ta="center">
                Back to login page
              </Text>
            </Group>
          </Anchor>
        </Box>
      </form>
    </Root>
  );
};

export default ResetPassForm;
